/**
 * TreatX Admin Dashboard
 * Main dashboard for platform administration
 * Uses CategoryManagementContext for dynamic categories
 * Sprint 10: Uses PackageCatalogContext for clinic CRM packages
 * P2.3: Full i18n coverage
 */
import React, { useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import { useTreatmentManagement } from '../../context/TreatmentManagementContext';
import { useCategoryManagement } from '../../context/CategoryManagementContext';
import { useProviderOnboarding, ONBOARDING_STATUS } from '../../context/ProviderOnboardingContext';
import { usePackageCatalog } from '../../context/PackageCatalogContext'; // Sprint 10: Dynamic packages
import { useContactInquiry, ContactInquiryProvider } from '../../context/ContactInquiryContext'; // Contact inquiries
import { ImageUploadField } from '../../components/ImageUploadField';
import { MultilingualInput } from '../../components/MultilingualInput';
import { getLocalizedField } from '../../context/LanguageContext';
import { formatPrice } from '../../config/packages';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Shield, LogOut, Settings, Plus, Search, Edit2, Trash2,
  DollarSign, Building2, Users, TrendingUp, Eye, EyeOff,
  Save, X, Image, Clock, Calendar, ChevronRight, Grid, ExternalLink,
  CheckCircle, AlertTriangle, HelpCircle, Globe, Heart, ChevronDown, ChevronUp, Package, MessageSquare, Mail, HeartPulse, ShieldCheck, FileText, MessageCircle, Wrench
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import { AftercarePanel } from '../../components/AftercarePanel';
import api from '../../utils/api';
// AdminDocumentRules removed — unified admin lives in AdminProviderDomain at /treatx-admin/provider-domain

// Assignment Overview Tab — Phase 1.5 System-Wide Visibility

// Upgrade Requests Panel — Enterprise plan requests visible to admin
const UpgradeRequestsPanel = () => {
  const [requests, setRequests] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get('/subscription/upgrade-requests');
        setRequests(data.requests || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await api.put(`/subscription/upgrade-requests/${id}`, { status: newStatus });
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
      toast({ title: 'Updated', description: `Request marked as ${newStatus}` });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    reviewed: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-amber-600" /> Enterprise Upgrade Requests
        </CardTitle>
      </CardHeader>
      <CardContent>
        {requests.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">No upgrade requests yet.</p>
        ) : (
          <div className="space-y-3" data-testid="upgrade-requests-list">
            {requests.map(req => (
              <div key={req.id} className="border rounded-lg p-4 flex items-start justify-between" data-testid={`upgrade-req-${req.id}`}>
                <div>
                  <p className="font-medium text-sm">{req.clinic_name || req.clinic_id}</p>
                  <p className="text-xs text-gray-500">{req.clinic_email} &middot; {req.requested_plan}</p>
                  {req.message && <p className="text-xs text-gray-600 mt-1 italic">&ldquo;{req.message}&rdquo;</p>}
                  <p className="text-xs text-gray-400 mt-1">{formatDateTimeLocale(req.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={statusColors[req.status] || 'bg-gray-100'}>{req.status}</Badge>
                  {req.status === 'pending' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(req.id, 'reviewed')} data-testid={`review-${req.id}`}>Review</Button>
                      <Button size="sm" className="bg-green-600 text-white" onClick={() => handleStatusUpdate(req.id, 'approved')} data-testid={`approve-${req.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleStatusUpdate(req.id, 'rejected')} data-testid={`reject-${req.id}`}>Reject</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AssignmentOverviewTab = () => {
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [unassignedLeads, setUnassignedLeads] = React.useState([]);
  const [recentLogs, setRecentLogs] = React.useState([]);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const headers = {};

      const [leadsRes] = await Promise.all([
        api.get('/api/assignments/unassigned/lead', { headers }),
      ]);
      setUnassignedLeads(leadsRes.data?.unassigned || []);

      // Get system-wide stats from DB
      const matrixRes = await api.get('/api/assignments/matrix', { headers });
      setStats({
        totalRoles: matrixRes.data?.roles?.length || 0,
        totalPermissions: matrixRes.data?.all_permissions?.length || 0,
      });
    } catch (err) {
      console.error('Failed to fetch assignment data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle data-testid="assignments-tab-title">Assignment System Overview</CardTitle>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-assignments-btn">
            <Search className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 text-center" data-testid="stat-roles">
            <p className="text-2xl font-bold text-blue-700">{stats?.totalRoles || '-'}</p>
            <p className="text-xs text-blue-600">RBAC Roles</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center" data-testid="stat-permissions">
            <p className="text-2xl font-bold text-green-700">{stats?.totalPermissions || '-'}</p>
            <p className="text-xs text-green-600">Permissions</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 text-center" data-testid="stat-unassigned-leads">
            <p className="text-2xl font-bold text-amber-700">{unassignedLeads.length}</p>
            <p className="text-xs text-amber-600">Unassigned Leads</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4 text-center" data-testid="stat-system-pool">
            <p className="text-2xl font-bold text-emerald-700">SYSTEM_POOL</p>
            <p className="text-xs text-emerald-600">Default Owner</p>
          </div>
        </div>

        {/* Unassigned Leads Table */}
        <div>
          <h3 className="font-semibold text-sm mb-3">Leads in System Pool (Need Assignment)</h3>
          {unassignedLeads.length === 0 ? (
            <div className="text-center py-6 bg-gray-50 rounded-lg">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">All leads have been assigned</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="unassigned-leads-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Lead ID</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Patient</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Clinic</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Owner</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {unassignedLeads.slice(0, 20).map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs font-mono">{lead.id?.slice(0, 8)}...</td>
                      <td className="px-4 py-2 text-xs">{lead.patient_name || '-'}</td>
                      <td className="px-4 py-2 text-xs">{lead.clinic_id || '-'}</td>
                      <td className="px-4 py-2">
                        <Badge variant="outline" className="text-[10px]">{lead.status}</Badge>
                      </td>
                      <td className="px-4 py-2">
                        <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                          {lead.assigned_agent_id === 'SYSTEM_POOL' ? 'System Pool' : lead.assigned_agent_id || 'None'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {unassignedLeads.length > 20 && (
                <p className="text-xs text-gray-500 text-center py-2">
                  Showing 20 of {unassignedLeads.length} unassigned leads
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Comprehensive Treatment Editor Modal with ALL content fields
const TreatmentEditorModal = ({ treatment, onSave, onClose, categories, t }) => {
  const [activeTab, setActiveTab] = useState('basic');
  
  // Initialize form with all fields
  const [formData, setFormData] = useState({
    name: treatment?.name || '',
    slug: treatment?.slug || '',
    category: treatment?.category || (categories[0]?.id || 'dental'),
    shortDescription: treatment?.shortDescription || '',
    image: treatment?.image || '',
    media_id: treatment?.media_id || null,
    images: treatment?.images || [],
    genderGroups: treatment?.genderGroups || ['women', 'men', 'non-binary', 'prefer-not-to-say'],
    status: treatment?.status || 'published',
    // Medical Content
    overview: treatment?.description?.overview || '',
    benefits: treatment?.description?.benefits || [''],
    risks: treatment?.description?.risks || [''],
    expectations: treatment?.description?.expectations || '',
    // Patient Motivation
    whoIsThisFor: treatment?.whoIsThisFor || [''],
    whyAbroad: treatment?.whyAbroad || [''],
    // FAQs
    faqs: treatment?.faqs || [{ question: '', answer: '' }],
    // Quick Info
    procedureDuration: treatment?.procedureDuration || '',
    recoveryTime: treatment?.recoveryTime || '',
    stayDuration: treatment?.stayDuration || '',
    // Pricing
    priceMode: treatment?.priceMode || 'auto',
    manualPriceMin: treatment?.manualPriceMin || '',
    manualPriceMax: treatment?.manualPriceMax || '',
    calculatedPriceMin: treatment?.calculatedPriceMin || 0,
    calculatedPriceMax: treatment?.calculatedPriceMax || 0,
    isActive: treatment?.isActive ?? true
  });

  // Auto-generate slug from name
  const handleNameChange = (name) => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setFormData({ ...formData, name, slug });
  };

  // List management helpers
  const addListItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const updateListItem = (field, index, value) => {
    const newList = [...formData[field]];
    newList[index] = value;
    setFormData({ ...formData, [field]: newList });
  };

  const removeListItem = (field, index) => {
    if (formData[field].length > 1) {
      const newList = formData[field].filter((_, i) => i !== index);
      setFormData({ ...formData, [field]: newList });
    }
  };

  // FAQ management
  const addFaq = () => {
    setFormData({ ...formData, faqs: [...formData.faqs, { question: '', answer: '' }] });
  };

  const updateFaq = (index, field, value) => {
    const newFaqs = [...formData.faqs];
    newFaqs[index][field] = value;
    setFormData({ ...formData, faqs: newFaqs });
  };

  const removeFaq = (index) => {
    if (formData.faqs.length > 1) {
      const newFaqs = formData.faqs.filter((_, i) => i !== index);
      setFormData({ ...formData, faqs: newFaqs });
    }
  };

  // Gender toggle
  const toggleGender = (gender) => {
    const newGenders = formData.genderGroups.includes(gender)
      ? formData.genderGroups.filter(g => g !== gender)
      : [...formData.genderGroups, gender];
    setFormData({ ...formData, genderGroups: newGenders });
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.shortDescription) {
      toast({ title: 'Error', description: 'Name and Short Description are required', variant: 'destructive' });
      return;
    }

    // Transform form data to treatment structure
    const treatmentData = {
      ...formData,
      description: {
        overview: formData.overview,
        benefits: formData.benefits.filter(b => b.trim()),
        risks: formData.risks.filter(r => r.trim()),
        expectations: formData.expectations
      },
      whoIsThisFor: formData.whoIsThisFor.filter(w => w.trim()),
      whyAbroad: formData.whyAbroad.filter(w => w.trim()),
      faqs: formData.faqs.filter(f => f.question.trim() && f.answer.trim())
    };

    onSave(treatmentData);
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Settings },
    { id: 'medical', label: 'Medical Content', icon: Heart },
    { id: 'motivation', label: 'Patient Motivation', icon: Globe },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'media', label: 'Media', icon: Image }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-700 to-pink-600 text-white p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">
                {treatment ? 'Edit Treatment' : 'Create New Treatment'}
              </h2>
              <p className="text-purple-200 text-sm mt-1">
                {treatment ? `Editing: ${getLocalizedField(treatment.name, 'en')}` : 'Fill in all content sections for the public treatment page'}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b flex-shrink-0">
          <div className="flex overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Treatment Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="e.g., Hair Transplant FUE"
                  />
                </div>
                <div>
                  <Label>URL Slug</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="auto-generated"
                    className="bg-gray-50"
                  />
                </div>
              </div>

              <div>
                <Label>Category *</Label>
                <select
                  className="w-full border rounded-lg p-2"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.emoji} {getLocalizedField(cat.name, 'en')}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Categories are managed in the Category Manager
                </p>
              </div>

              <div>
                <Label>Short Description * <span className="text-gray-400">({formData.shortDescription.length}/150)</span></Label>
                <Input
                  value={formData.shortDescription}
                  onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value.slice(0, 150) })}
                  placeholder="Brief description shown on cards"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Procedure Duration</Label>
                  <Input
                    value={formData.procedureDuration}
                    onChange={(e) => setFormData({ ...formData, procedureDuration: e.target.value })}
                    placeholder="e.g., 6-8 hours"
                  />
                </div>
                <div>
                  <Label>Recovery Time</Label>
                  <Input
                    value={formData.recoveryTime}
                    onChange={(e) => setFormData({ ...formData, recoveryTime: e.target.value })}
                    placeholder="e.g., 7-10 days"
                  />
                </div>
                <div>
                  <Label>Recommended Stay</Label>
                  <Input
                    value={formData.stayDuration}
                    onChange={(e) => setFormData({ ...formData, stayDuration: e.target.value })}
                    placeholder="e.g., 3-5 days"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Gender Classification</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'women', label: 'Women' },
                    { id: 'men', label: 'Men' },
                    { id: 'non-binary', label: 'Non-binary' },
                    { id: 'prefer-not-to-say', label: 'Prefer not to say' }
                  ].map(g => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => toggleGender(g.id)}
                      className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                        formData.genderGroups.includes(g.id)
                          ? 'bg-purple-100 border-purple-500 text-purple-700'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Publication Status</Label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'draft' })}
                    className={`flex-1 p-4 rounded-lg border-2 ${
                      formData.status === 'draft' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'
                    }`}
                  >
                    <EyeOff className="h-5 w-5 mx-auto mb-2 text-amber-600" />
                    <p className="font-medium">Draft</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'published' })}
                    className={`flex-1 p-4 rounded-lg border-2 ${
                      formData.status === 'published' ? 'border-green-500 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <Eye className="h-5 w-5 mx-auto mb-2 text-green-600" />
                    <p className="font-medium">Published</p>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Medical Content Tab */}
          {activeTab === 'medical' && (
            <div className="space-y-6">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Shield className="h-4 w-4 text-blue-600" />
                  Overview *
                </Label>
                <Textarea
                  value={formData.overview}
                  onChange={(e) => setFormData({ ...formData, overview: e.target.value })}
                  placeholder="Comprehensive overview of the treatment, procedure details, and what makes it effective..."
                  rows={5}
                />
              </div>

              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Benefits
                </Label>
                {formData.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <Input
                      value={benefit}
                      onChange={(e) => updateListItem('benefits', idx, e.target.value)}
                      placeholder="Enter a benefit..."
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeListItem('benefits', idx)}
                      className="text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addListItem('benefits')}>
                  <Plus className="h-4 w-4 mr-1" /> Add Benefit
                </Button>
              </div>

              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  Potential Risks & Considerations
                </Label>
                {formData.risks.map((risk, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <Input
                      value={risk}
                      onChange={(e) => updateListItem('risks', idx, e.target.value)}
                      placeholder="Enter a risk or consideration..."
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeListItem('risks', idx)}
                      className="text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addListItem('risks')}>
                  <Plus className="h-4 w-4 mr-1" /> Add Risk
                </Button>
              </div>

              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  What to Expect
                </Label>
                <Textarea
                  value={formData.expectations}
                  onChange={(e) => setFormData({ ...formData, expectations: e.target.value })}
                  placeholder="Describe the patient journey: consultation, procedure day, recovery timeline..."
                  rows={4}
                />
              </div>

              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Heart className="h-4 w-4 text-pink-600" />
                  Who Is This For?
                </Label>
                {formData.whoIsThisFor.map((item, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <Input
                      value={item}
                      onChange={(e) => updateListItem('whoIsThisFor', idx, e.target.value)}
                      placeholder="Describe ideal candidate..."
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeListItem('whoIsThisFor', idx)}
                      className="text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addListItem('whoIsThisFor')}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </div>
            </div>
          )}

          {/* Patient Motivation Tab */}
          {activeTab === 'motivation' && (
            <div className="space-y-6">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Globe className="h-4 w-4 text-blue-600" />
                  Why Patients Choose This Treatment Abroad
                </Label>
                <p className="text-sm text-gray-500 mb-3">Marketing-focused reasons for medical tourism</p>
                {formData.whyAbroad.map((reason, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <Input
                      value={reason}
                      onChange={(e) => updateListItem('whyAbroad', idx, e.target.value)}
                      placeholder="e.g., Save 50-70% compared to US prices"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeListItem('whyAbroad', idx)}
                      className="text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addListItem('whyAbroad')}>
                  <Plus className="h-4 w-4 mr-1" /> Add Reason
                </Button>
              </div>

              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <HelpCircle className="h-4 w-4 text-purple-600" />
                  Frequently Asked Questions
                </Label>
                <p className="text-sm text-gray-500 mb-3">Add common questions patients ask about this treatment</p>
                {formData.faqs.map((faq, idx) => (
                  <Card key={idx} className="mb-4">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-2">
                        <Label className="text-sm">FAQ #{idx + 1}</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFaq(idx)}
                          className="text-red-500 h-6"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <Input
                        value={faq.question}
                        onChange={(e) => updateFaq(idx, 'question', e.target.value)}
                        placeholder="Question..."
                        className="mb-2"
                      />
                      <Textarea
                        value={faq.answer}
                        onChange={(e) => updateFaq(idx, 'answer', e.target.value)}
                        placeholder="Answer..."
                        rows={3}
                      />
                    </CardContent>
                  </Card>
                ))}
                <Button type="button" variant="outline" onClick={addFaq}>
                  <Plus className="h-4 w-4 mr-1" /> Add FAQ
                </Button>
              </div>
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <div className="space-y-6">
              <div>
                <Label className="mb-3 block">Pricing Mode</Label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, priceMode: 'auto' })}
                    className={`flex-1 p-4 rounded-lg border-2 ${
                      formData.priceMode === 'auto' ? 'border-green-500 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <p className="font-medium">Auto (from Clinics)</p>
                    <p className="text-xs text-gray-500">Calculated from clinic prices</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, priceMode: 'manual' })}
                    className={`flex-1 p-4 rounded-lg border-2 ${
                      formData.priceMode === 'manual' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                    }`}
                  >
                    <p className="font-medium">Manual Override</p>
                    <p className="text-xs text-gray-500">Set your own price range</p>
                  </button>
                </div>
              </div>

              {formData.priceMode === 'manual' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Minimum Price (EUR)</Label>
                    <Input
                      type="number"
                      value={formData.manualPriceMin}
                      onChange={(e) => setFormData({ ...formData, manualPriceMin: e.target.value })}
                      placeholder="e.g., 1500"
                    />
                  </div>
                  <div>
                    <Label>Maximum Price (EUR)</Label>
                    <Input
                      type="number"
                      value={formData.manualPriceMax}
                      onChange={(e) => setFormData({ ...formData, manualPriceMax: e.target.value })}
                      placeholder="e.g., 5000"
                    />
                  </div>
                </div>
              )}

              <Card className="bg-gray-50">
                <CardContent className="pt-4">
                  <p className="text-sm font-medium mb-2">Price Preview</p>
                  <p className="text-2xl font-bold text-green-600">
                    €{formData.priceMode === 'manual' && formData.manualPriceMin 
                      ? parseInt(formData.manualPriceMin).toLocaleString() 
                      : formData.calculatedPriceMin.toLocaleString()
                    } - €{formData.priceMode === 'manual' && formData.manualPriceMax 
                      ? parseInt(formData.manualPriceMax).toLocaleString() 
                      : formData.calculatedPriceMax.toLocaleString()
                    }
                  </p>
                  <Badge className={formData.priceMode === 'auto' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}>
                    {formData.priceMode === 'auto' ? 'Auto-calculated' : 'Manual Override'}
                  </Badge>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Media Tab */}
          {activeTab === 'media' && (
            <div className="space-y-6">
              <div>
                <Label className="mb-2 block">Cover Image</Label>
                <ImageUploadField
                  currentImage={formData.image}
                  mediaId={formData.media_id}
                  assetType="treatment_image"
                  onUploadComplete={(asset) => {
                    setFormData(prev => ({
                      ...prev,
                      image: asset.public_url || asset.signed_url || prev.image,
                      media_id: asset.id,
                    }));
                  }}
                  onClear={() => setFormData(prev => ({ ...prev, image: '', media_id: null }))}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex gap-3 flex-shrink-0 bg-gray-50">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600">
            <Save className="h-4 w-4 mr-2" />
            {treatment ? 'Update Treatment' : 'Create Treatment'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Campaign Alert Stats for admin analytics tab
const CampaignAlertStatsCard = () => {
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const token = '' || localStorage.getItem('treatx_admin_token');

  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get(`/admin/analytics/campaign-alert-stats`, {
        });
        {
          const data = res.data;
          setStats(data.campaign_alert_stats);
        }
      } catch { /* silent */ }
      setLoading(false);
    };
    fetchStats();
  }, [token]);

  if (loading || !stats) return null;

  return (
    <Card className="border-2 border-red-200 bg-gradient-to-br from-red-50 to-orange-50 mt-4" data-testid="campaign-alert-stats-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          Campaign Alert Statistics
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          Automated campaign performance alerts overview
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-lg p-3 border text-center">
            <p className="text-2xl font-bold text-red-600" data-testid="stat-alerts-triggered">{stats.alerts_triggered}</p>
            <p className="text-xs text-slate-500">Alerts Triggered</p>
          </div>
          <div className="bg-white rounded-lg p-3 border text-center">
            <p className="text-2xl font-bold text-emerald-600" data-testid="stat-alerts-resolved">{stats.alerts_resolved}</p>
            <p className="text-xs text-slate-500">Alerts Resolved</p>
          </div>
          <div className="bg-white rounded-lg p-3 border text-center">
            <p className="text-2xl font-bold text-amber-600" data-testid="stat-alerts-unresolved">{stats.alerts_unresolved}</p>
            <p className="text-xs text-slate-500">Unresolved</p>
          </div>
          <div className="bg-white rounded-lg p-3 border text-center">
            <p className="text-2xl font-bold text-[#122033]" data-testid="stat-critical-campaigns">{stats.critical_campaign_count}</p>
            <p className="text-xs text-slate-500">Critical Campaigns</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const TreatXAdminDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { admin, logout } = useTreatXAdminAuth();
  const { 
    getAllTreatments,
    addTreatment,
    updateTreatment,
    deleteTreatment,
    fetchTreatments,
  } = useTreatmentManagement();
  const { getActiveCategories, getAllCategories } = useCategoryManagement();
  
  // Item 6: Get provider data for consistent metrics
  const { providers, pendingApplications } = useProviderOnboarding();
  
  // Sprint 10: Get dynamic package catalog for Clinic CRM packages
  const { getAllPackages, getAvailablePackages, refreshCatalog } = usePackageCatalog();
  const allCatalogPackages = getAllPackages();
  const activeCatalogPackages = getAvailablePackages();

  // PRE-UAT-DATA-VISIBILITY-FIX-01 — both PackageCatalogContext and
  // TreatmentManagementContext mount at app root BEFORE the admin's
  // unified token is stored in localStorage. The first fetch is
  // therefore made anonymously and either returns 0 rows (treatments)
  // or is blocked by the soft-launch gate (packages). Neither context
  // re-fetches on its own. Re-running both fetches from the admin
  // dashboard mount guarantees authenticated reads against the
  // canonical SSOT endpoints. No new endpoint, no parallel SSOT, no
  // hardcoded fallback.
  React.useEffect(() => {
    refreshCatalog?.();
    fetchTreatments?.();
    // Intentionally fire once on dashboard mount. The contexts expose
    // their own write-side refreshers for ongoing edits.
  }, []);

  const [activeTab, setActiveTab] = useState('treatments');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editModal, setEditModal] = useState({ open: false, treatment: null });

  // BLOCKER 3 FIX: Fetch application stats from SSOT backend endpoint
  const [appStats, setAppStats] = React.useState({ total: 0, submitted: 0, under_review: 0, info_requested: 0, resubmitted: 0, approved: 0, rejected: 0 });
  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await api.get('/admin/applications/stats');
        if (data.success && data.stats) setAppStats(data.stats);
      } catch (err) { console.error('[Dashboard] stats fetch error:', err); }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, []);

  // Redirect if not logged in - use useEffect to avoid calling navigate during render
  // Note: RBACProtectedRoute already handles authentication.
  // This is a fallback for the legacy TreatXAdminAuth context.
  React.useEffect(() => {
    if (!admin) {
      // Check RBAC state before redirecting — RBACProtectedRoute may have already validated
      const perms = localStorage.getItem('treatx_rbac_permissions');
      if (!perms || perms === '[]') {
        navigate('/login');
      }
    }
  }, [admin, navigate]);

  // Show nothing while redirecting — only if truly unauthenticated
  if (!admin && !(localStorage.getItem('treatx_rbac_permissions') || '[]').includes('content.manage')) {
    return null;
  }

  const allTreatments = getAllTreatments();
  const allCategories = getAllCategories();
  const activeCategories = getActiveCategories();

  // Filter treatments
  const filteredTreatments = allTreatments.filter(t => {
    const descriptionText = typeof t.description === 'object' 
      ? (t.description?.overview || t.shortDescription || '') 
      : (t.description || t.shortDescription || '');
    const matchesSearch = getLocalizedField(t.name, 'en').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          getLocalizedField(descriptionText, 'en').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // BLOCKER 3 FIX: Stats derived from single SSOT backend (provider_applications)
  const pendingCount = (appStats.submitted || 0) + (appStats.under_review || 0) + (appStats.resubmitted || 0) + (appStats.info_requested || 0);
  const stats = {
    totalTreatments: allTreatments.length,
    activeTreatments: allTreatments.filter(t => t.isActive).length,
    totalClinics: appStats.approved || 0,
    totalPatients: 0,
    pendingApplications: pendingCount,
    totalApplications: appStats.total || 0,
    totalProviders: appStats.total || 0,
  };

  const handleLogout = () => {
    logout();
    navigate('/treatx-admin/login');
  };

  const handleSaveTreatment = (data) => {
    if (editModal.treatment) {
      updateTreatment(editModal.treatment.id, data);
      toast({ title: 'Treatment Updated', description: `"${getLocalizedField(data.name, 'en')}" has been updated.` });
    } else {
      addTreatment(data);
      toast({ title: 'Treatment Created', description: `"${getLocalizedField(data.name, 'en')}" has been created.` });
    }
    setEditModal({ open: false, treatment: null });
  };

  const handleDeleteTreatment = (treatment) => {
    if (window.confirm(`Are you sure you want to delete "${getLocalizedField(treatment.name, 'en')}"?`)) {
      deleteTreatment(treatment.id);
      toast({ title: 'Treatment Deleted', description: `"${getLocalizedField(treatment.name, 'en')}" has been deleted.` });
    }
  };

  // Get category display info
  const getCategoryInfo = (categoryId) => {
    const cat = allCategories.find(c => c.id === categoryId);
    return cat || { id: categoryId, name: categoryId, emoji: '📋' };
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      
      {/* P3-1 obsolete build banner removed (PRE-UAT-CLEANUP-MOBILE-LAYOUT-01)
          The empty fixed red banner was eating ~24px above the purple
          header and forcing it onto a second visual row on mobile.
          Removing it lets the sticky header sit at top:0 and the rest
          of the layout flow naturally. */}

      {/* P3-1: Header focused on "Which providers / packages need action?" */}
      <header className="bg-gradient-to-r from-purple-800 to-pink-900 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-xl font-bold truncate" data-testid="treatx-admin-title">Platform Admin</h1>
                <p className="text-xs sm:text-sm text-purple-200 truncate hidden sm:block">Manage providers, packages & treatments</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <div className="flex items-center gap-2">
                <img 
                  src={admin?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(admin?.name || 'Admin')}&background=8B5CF6&color=fff`} 
                  alt={admin?.name || 'Admin'}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="hidden sm:block">
                  <p className="text-sm font-medium">{admin?.name || 'TreatX Admin'}</p>
                  <p className="text-xs text-purple-200">{admin?.title || 'Platform Administrator'}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-purple-200 hover:text-white hover:bg-white/10 px-2 sm:px-3"
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* P3-1: Primary Action Banner - Pending Provider Applications */}
      {stats.pendingApplications > 0 && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800" data-testid="pending-providers-count">
                    {stats.pendingApplications} provider application{stats.pendingApplications > 1 ? 's' : ''} pending review
                  </p>
                  <p className="text-sm text-amber-600">Requires admin action</p>
                </div>
              </div>
              <Button 
                size="sm" 
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => navigate('/treatx-admin/providers')}
                data-testid="review-providers-btn"
              >
                Review Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Simplified Stats Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            <div className="text-center cursor-pointer hover:bg-slate-50 rounded p-2" data-testid="stat-providers">
              <p className="text-lg sm:text-xl font-bold text-purple-600">{stats.totalProviders}</p>
              <p className="text-xs text-slate-500">Providers</p>
            </div>
            <div className="text-center cursor-pointer hover:bg-slate-50 rounded p-2" onClick={() => navigate('/treatx-admin/providers')} data-testid="stat-pending">
              <p className="text-lg sm:text-xl font-bold text-amber-600">{stats.pendingApplications}</p>
              <p className="text-xs text-slate-500">Pending</p>
            </div>
            <div className="text-center cursor-pointer hover:bg-slate-50 rounded p-2" onClick={() => navigate('/treatx-admin/treatments')} data-testid="stat-treatments">
              <p className="text-lg sm:text-xl font-bold text-green-600">{stats.activeTreatments}</p>
              <p className="text-xs text-slate-500">Treatments</p>
            </div>
            <div className="text-center cursor-pointer hover:bg-slate-50 rounded p-2" onClick={() => navigate('/treatx-admin/packages')} data-testid="stat-packages">
              <p className="text-lg sm:text-xl font-bold text-blue-600">{activeCatalogPackages.length}</p>
              <p className="text-xs text-slate-500">Packages</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 overflow-x-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* PRE-UAT-CLEANUP-MOBILE-LAYOUT-01: on mobile use horizontal
              scroll (no wrap, no truncated labels) — readable tap
              targets, no awkward 3-row wrap. Desktop keeps the
              wrapping grid behaviour. */}
          <TabsList className="mb-6 flex flex-nowrap sm:flex-wrap overflow-x-auto sm:overflow-visible h-auto gap-1 justify-start scrollbar-thin">
            <TabsTrigger value="clinics" data-testid="tab-clinics">
              <Building2 className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.providers', 'Providers')}
            </TabsTrigger>
            {/* TREATX-WEBSITE-MGMT-01 — the public website previously had no tab
                of its own; its four cards were buried under Providers. */}
            <TabsTrigger value="website" data-testid="tab-website">
              <Globe className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.website', 'Website')}
            </TabsTrigger>
            <TabsTrigger value="packages" data-testid="tab-packages">
              <Package className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.packages', 'Packages')}
            </TabsTrigger>
            <TabsTrigger value="treatments" data-testid="tab-treatments">
              <Settings className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.treatments', 'Treatments')}
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">
              <Grid className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.categories', 'Categories')}
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <TrendingUp className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.analytics', 'Analytics')}
            </TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">
              <MessageSquare className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.inquiries', 'Inquiries')}
            </TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">
              <Building2 className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.applications', 'Applications')}
            </TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails">
              <Mail className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.emailTemplates', 'Email Templates')}
            </TabsTrigger>
            <TabsTrigger value="messaging" data-testid="tab-messaging">
              <MessageSquare className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.messaging', 'Messaging')}
            </TabsTrigger>
            <TabsTrigger value="assignments" data-testid="tab-assignments">
              <Users className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.assignments', 'Assignments')}
            </TabsTrigger>
            <TabsTrigger value="aftercare" data-testid="tab-aftercare">
              <HeartPulse className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.aftercare', 'Aftercare')}
            </TabsTrigger>
            <TabsTrigger value="ba-moderation" data-testid="tab-ba-moderation" onClick={() => navigate('/treatx-admin/before-after-moderation')}>
              <ShieldCheck className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.beforeAfter', 'Before/After')}
            </TabsTrigger>
            <TabsTrigger value="logo-moderation" data-testid="tab-logo-moderation" onClick={() => navigate('/treatx-admin/logo-moderation')}>
              <Shield className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.logos', 'Logos')}
            </TabsTrigger>
            <TabsTrigger value="facility-media-moderation" data-testid="tab-facility-media-moderation" onClick={() => navigate('/treatx-admin/facility-media-moderation')}>
              <Shield className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.facilityPhotos', 'Facility Photos')}
            </TabsTrigger>
            <TabsTrigger value="soft-launch-invites" data-testid="tab-soft-launch-invites" onClick={() => navigate('/treatx-admin/soft-launch-invites')}>
              <ShieldCheck className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.invites', 'Invites')}
            </TabsTrigger>
            {admin?.role === 'treatx_super_admin' && (
              <TabsTrigger value="launch-control" data-testid="tab-launch-control" onClick={() => navigate('/treatx-admin/launch-control')}>
                <ShieldCheck className="h-4 w-4 mr-2" />
                {t('treatxAdmin.tabs.launchControl', 'Launch Control')}
              </TabsTrigger>
            )}
            <TabsTrigger value="escrow" data-testid="tab-escrow" onClick={() => navigate('/treatx-admin/escrow')}>
              <TrendingUp className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.escrow', 'Escrow')}
            </TabsTrigger>
            <TabsTrigger value="finance" data-testid="tab-admin-finance" onClick={() => navigate('/treatx-admin/finance')}>
              <DollarSign className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.finance', 'Finance')}
            </TabsTrigger>
            <TabsTrigger value="corp-identity" data-testid="tab-corporate-identity" onClick={() => navigate('/treatx-admin/corporate-identity')}>
              <Shield className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.corporateIdentity', 'Corporate Identity')}
            </TabsTrigger>
            <TabsTrigger value="agreements" data-testid="tab-agreements" onClick={() => navigate('/treatx-admin/agreements')}>
              <FileText className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.agreements', 'Agreements')}
            </TabsTrigger>
            <TabsTrigger value="upgrade-requests" data-testid="tab-upgrade-requests">
              <TrendingUp className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.upgradeRequests', 'Upgrade Requests')}
            </TabsTrigger>
            <TabsTrigger value="document-rules" data-testid="tab-document-rules">
              <Shield className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.documentRules', 'Document Rules')}
            </TabsTrigger>
            <TabsTrigger value="user-roles" data-testid="tab-user-roles" onClick={() => navigate('/treatx-admin/user-roles')}>
              <Users className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.userRoles', 'User Roles')}
            </TabsTrigger>
            {/* TREATX-MANAGED-RUNTIME-CONTENT-01 — Super-Admin-only Content Management */}
            {admin?.role === 'treatx_super_admin' && (
              <TabsTrigger value="content-management" data-testid="tab-content-management" onClick={() => navigate('/treatx-admin/content-management')}>
                <FileText className="h-4 w-4 mr-2" />
                {t('treatxAdmin.tabs.contentManagement', 'Content Management')}
              </TabsTrigger>
            )}
            {/* TREATX-ADMIN-MAINTENANCE-01 — Super-Admin-only maintenance operations */}
            {admin?.role === 'treatx_super_admin' && (
              <TabsTrigger value="maintenance" data-testid="tab-maintenance" onClick={() => navigate('/treatx-admin/maintenance')}>
                <Wrench className="h-4 w-4 mr-2" />
                {t('treatxAdmin.tabs.maintenance', 'Maintenance')}
              </TabsTrigger>
            )}
            {/* TREATX-WA-DIAGNOSTIC-TEST-01 — temporary super-admin-only WhatsApp test (removable after UAT) */}
            <TabsTrigger value="whatsapp-test" data-testid="tab-whatsapp-test" onClick={() => navigate('/treatx-admin/whatsapp-test')}>
              <MessageCircle className="h-4 w-4 mr-2" />
              {t('treatxAdmin.tabs.whatsappTest', 'WhatsApp Test')}
            </TabsTrigger>
          </TabsList>

          {/* Treatments Tab */}
          <TabsContent value="treatments">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Treatment Management</CardTitle>
                  <Button 
                    onClick={() => setEditModal({ open: true, treatment: null })}
                    className="bg-gradient-to-r from-purple-600 to-pink-600"
                    data-testid="add-treatment-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Treatment
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search treatments..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <select
                    className="border rounded-lg px-4 py-2 min-w-[180px]"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="all">All Categories</option>
                    {activeCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.emoji} {getLocalizedField(cat.name, 'en')}</option>
                    ))}
                  </select>
                </div>

                {/* Treatments Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-semibold text-slate-600">Treatment</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-600">Category</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-600">Price Range</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-600">Content</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-600">Status</th>
                        <th className="text-right p-3 text-sm font-semibold text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTreatments.map(treatment => {
                        const category = getCategoryInfo(treatment.category);
                        const hasFullContent = treatment.description?.overview && 
                                               treatment.description?.benefits?.length > 0 &&
                                               treatment.faqs?.length > 0;
                        return (
                          <tr key={treatment.id} className="border-t hover:bg-slate-50">
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                <img 
                                  src={treatment.image} 
                                  alt={getLocalizedField(treatment.name, 'en')}
                                  className="w-12 h-12 rounded-lg object-cover"
                                />
                                <div>
                                  <p className="font-medium">{getLocalizedField(treatment.name, 'en')}</p>
                                  <p className="text-xs text-slate-500 truncate max-w-[200px]">
                                    {getLocalizedField(treatment.shortDescription, 'en') || getLocalizedField(treatment.description?.overview, 'en') || ''}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="p-3">
                              <Badge variant="outline">
                                {category.emoji} {getLocalizedField(category.name, 'en')}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <span className="font-medium text-green-600">
                                €{(treatment.priceMode === 'manual' && treatment.manualPriceMin 
                                  ? treatment.manualPriceMin 
                                  : treatment.calculatedPriceMin || 0).toLocaleString()} - 
                                €{(treatment.priceMode === 'manual' && treatment.manualPriceMax 
                                  ? treatment.manualPriceMax 
                                  : treatment.calculatedPriceMax || 0).toLocaleString()}
                              </span>
                            </td>
                            <td className="p-3">
                              <Badge className={hasFullContent ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                                {hasFullContent ? 'Complete' : 'Incomplete'}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <Badge className={treatment.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                                {treatment.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <div className="flex justify-end gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => setEditModal({ open: true, treatment })}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteTreatment(treatment)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Treatment Categories</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      Define and manage categories for organizing treatments
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate('/treatx-admin/categories')}
                    className="bg-gradient-to-r from-purple-600 to-pink-600"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Category Manager
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {allCategories.map(category => (
                    <div 
                      key={category.id}
                      className="p-4 bg-slate-50 rounded-lg text-center hover:bg-slate-100 transition-colors"
                    >
                      <span className="text-3xl">{category.emoji}</span>
                      <p className="font-medium mt-2">{getLocalizedField(category.name, 'en')}</p>
                      <p className="text-xs text-slate-500">
                        {category.treatmentCount || allTreatments.filter(t => t.category === category.id).length} treatments
                      </p>
                    </div>
                  ))}
                </div>
                <p className="text-center text-sm text-slate-500 mt-6">
                  Use the Category Manager to create new categories, edit icons, and add images.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clinics Tab */}
          <TabsContent value="clinics">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Provider Management Card */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Provider Management</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        Review applications and manage approved providers
                      </p>
                    </div>
                    <Button 
                      onClick={() => navigate('/treatx-admin/provider-domain')}
                      className="bg-gradient-to-r from-purple-600 to-pink-600"
                      data-testid="open-provider-panel-btn"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open Provider Panel
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-amber-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-amber-600">{stats.pendingApplications}</p>
                      <p className="text-xs text-amber-700">Pending</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{stats.totalClinics}</p>
                      <p className="text-xs text-green-700">Approved</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">{stats.totalProviders}</p>
                      <p className="text-xs text-blue-700">Total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Clinic CRM Package Management Card - Sprint 10 */}
              <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <span className="text-xl">📦</span>
                        Provider Plans
                      </CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        Manage subscription packages for clinic CRM
                      </p>
                    </div>
                    <Button 
                      onClick={() => navigate('/treatx-admin/packages')}
                      className="bg-gradient-to-r from-purple-600 to-pink-600"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Manage Packages
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 bg-white p-3 rounded-lg text-center border">
                      <p className="text-2xl font-bold text-purple-600">{activeCatalogPackages.length}</p>
                      <p className="text-xs text-slate-500">Active Packages</p>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-slate-600">
                        Define pricing, features, and limits for clinic CRM subscription tiers.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Positioning Tags Governance Card - D1.1 */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Positioning Tags</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        Manage the tag catalog and review clinic suggestions
                      </p>
                    </div>
                    <Button 
                      onClick={() => navigate('/treatx-admin/positioning-tags')}
                      className="bg-gradient-to-r from-violet-600 to-purple-600"
                      data-testid="open-positioning-tags"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Manage Tags
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">Control which positioning tags clinics can display. Review and approve new tag suggestions.</p>
                </CardContent>
              </Card>

              {/* VIP Services Governance Card - P6F */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>VIP Services</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        Manage the VIP service catalog available to providers
                      </p>
                    </div>
                    <Button
                      onClick={() => navigate('/treatx-admin/vip-services')}
                      className="bg-gradient-to-r from-violet-600 to-purple-600"
                      data-testid="open-vip-services"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Manage VIP Services
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">Control the VIP services (transport, accommodation, support) providers can offer. Soft-archive to hide without deletion.</p>
                </CardContent>
              </Card>

              {/* Treatment Taxonomy Card */}
              <Card className="border-2 border-teal-200" data-testid="treatment-taxonomy-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Treatment Taxonomy</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        Manage categories and canonical treatments
                      </p>
                    </div>
                    <Button 
                      onClick={() => navigate('/treatx-admin/treatment-taxonomy')}
                      className="bg-gradient-to-r from-teal-600 to-cyan-600"
                      data-testid="open-treatment-taxonomy"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Manage Taxonomy
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">Full CRUD on treatment categories and the canonical treatment catalog. Controls search filters and normalization.</p>
                </CardContent>
              </Card>

              {/* Document Rules Card */}
              <Card className="border-2 border-indigo-200" data-testid="document-rules-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Document Rules</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        Configure document types and requirement rules for onboarding
                      </p>
                    </div>
                    <Button 
                      onClick={() => navigate('/treatx-admin/provider-domain')}
                      className="bg-gradient-to-r from-indigo-600 to-purple-600"
                      data-testid="open-document-rules"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Manage Rules
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">Manage document types master catalog and requirement rules that define what documents are needed for provider verification.</p>
                </CardContent>
              </Card>

              {/* Commission Rules Card */}
              <Card className="border-2 border-amber-200" data-testid="commission-rules-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Commission Rules</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        Configure commission by country, clinic, package, and more
                      </p>
                    </div>
                    <Button 
                      onClick={() => navigate('/treatx-admin/commission-rules')}
                      className="bg-gradient-to-r from-amber-600 to-orange-600"
                      data-testid="open-commission-rules"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Manage Commission
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">Define variable commission rates by market, clinic, package, treatment, campaign, lead source, and legal entity. Preview resolution before applying.</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TREATX-WEBSITE-MGMT-01 — the public website gets its own tab. Its
              four cards previously sat under Providers, which is a large part
              of why the website felt scattered. Every capability is preserved;
              the old routes still resolve and land on the matching tab. */}
          <TabsContent value="website">
            <div className="grid gap-6">
              <Card className="border-2 border-sky-200" data-testid="website-management-card">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <CardTitle>Website Management</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        Everything shown on the public website, in one place
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => navigate('/treatx-admin/translations')}
                        data-testid="open-translations"
                      >
                        <Globe className="h-4 w-4 mr-2" />
                        Translations
                      </Button>
                      <Button
                        onClick={() => navigate('/treatx-admin/website-management')}
                        className="bg-gradient-to-r from-sky-600 to-blue-600"
                        data-testid="open-website-management"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Manage Website
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">
                    Edit the homepage and the provider benefits page, upload images, then
                    preview and publish — organised as three tabs.
                  </p>
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <button
                      type="button"
                      onClick={() => navigate('/treatx-admin/website-management?tab=homepage')}
                      className="rounded-lg border border-slate-200 p-3 text-left transition-colors hover:border-sky-300 hover:bg-sky-50"
                      data-testid="open-wm-homepage"
                    >
                      <p className="text-sm font-medium text-slate-900">Homepage</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        Hero, banners, sections, images and the statistics strip
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/treatx-admin/website-management?tab=provider-benefits')}
                      className="rounded-lg border border-slate-200 p-3 text-left transition-colors hover:border-sky-300 hover:bg-sky-50"
                      data-testid="open-wm-provider-benefits"
                    >
                      <p className="text-sm font-medium text-slate-900">Provider Benefits</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        Hero, benefit rows, closing call-to-action and images
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/treatx-admin/website-management?tab=preview-publish')}
                      className="rounded-lg border border-slate-200 p-3 text-left transition-colors hover:border-sky-300 hover:bg-sky-50"
                      data-testid="open-wm-preview-publish"
                    >
                      <p className="text-sm font-medium text-slate-900">Preview &amp; Publish</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        Homepage version, V3 candidate review, release readiness
                      </p>
                    </button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Packages Tab - Sprint 10: Dynamic Clinic CRM Packages */}
          <TabsContent value="packages">
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                      <span className="text-2xl">📦</span>
                      Package Catalog
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      Manage subscription packages for clinic CRM (Trial, Starter, Pro, Enterprise)
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate('/treatx-admin/packages')}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 w-full sm:w-auto"
                    size="lg"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    <span className="truncate">Open Package Manager</span>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
                  <div className="bg-white p-3 sm:p-4 rounded-lg text-center border shadow-sm">
                    <p className="text-2xl sm:text-3xl font-bold text-purple-600">{allCatalogPackages.length}</p>
                    <p className="text-xs sm:text-sm text-slate-500">Total Packages</p>
                  </div>
                  <div className="bg-white p-3 sm:p-4 rounded-lg text-center border shadow-sm">
                    <p className="text-2xl sm:text-3xl font-bold text-green-600">{activeCatalogPackages.length}</p>
                    <p className="text-xs sm:text-sm text-slate-500">Active & Public</p>
                  </div>
                  <div className="bg-white p-3 sm:p-4 rounded-lg text-center border shadow-sm">
                    <p className="text-2xl sm:text-3xl font-bold text-gray-400">{allCatalogPackages.filter(p => !p.active).length}</p>
                    <p className="text-xs sm:text-sm text-slate-500">Inactive</p>
                  </div>
                  <div className="bg-white p-3 sm:p-4 rounded-lg text-center border shadow-sm">
                    <p className="text-2xl sm:text-3xl font-bold text-blue-600">{allCatalogPackages.filter(p => !p.public).length}</p>
                    <p className="text-xs sm:text-sm text-slate-500">Private</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 sm:p-4 border">
                  <h4 className="font-medium text-gray-700 mb-3">Provider Plans (Dynamic Catalog)</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                    {allCatalogPackages.map(pkg => (
                      <div 
                        key={pkg.id} 
                        className={`text-center p-3 rounded-lg border ${
                          pkg.active && pkg.public 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-gray-50 border-gray-200 opacity-60'
                        }`}
                      >
                        <p className="font-semibold text-gray-700">{pkg.displayName}</p>
                        <p className="text-xs text-gray-500">{pkg.packageKey || pkg.package_key}</p>
                        <div className="flex gap-1 justify-center mt-1">
                          {pkg.active && <Badge variant="outline" className="text-xs">Active</Badge>}
                          {pkg.public && <Badge variant="outline" className="text-xs">Public</Badge>}
                          {!pkg.active && <Badge variant="outline" className="text-xs text-gray-400">Inactive</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Note: Only Active & Public packages are visible to clinics in the upgrade flow.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <TrendingUp className="h-5 w-5 text-teal-600" />
                      Platform Analytics
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      Revenue, funnel metrics, and top clinic performance
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate('/treatx-admin/analytics')}
                    className="bg-gradient-to-r from-teal-600 to-cyan-600"
                    data-testid="open-analytics-dashboard-btn"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Analytics Dashboard
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-white rounded-lg p-6 border text-center">
                  <TrendingUp className="h-12 w-12 text-teal-400 mx-auto mb-3" />
                  <h4 className="font-medium text-gray-700 mb-2">Advanced Analytics Dashboard</h4>
                  <p className="text-sm text-gray-500 max-w-md mx-auto">
                    View real-time financial metrics, conversion funnels, top-performing clinics,
                    and a live ledger health heartbeat.
                  </p>
                  <div className="flex justify-center gap-4 mt-4 flex-wrap">
                    <div className="bg-teal-50 px-3 py-2 rounded-lg">
                      <p className="text-sm font-semibold text-teal-600">KPI Tiles</p>
                      <p className="text-xs text-gray-500">Leads, Quotes, Bookings</p>
                    </div>
                    <div className="bg-blue-50 px-3 py-2 rounded-lg">
                      <p className="text-sm font-semibold text-blue-600">Funnel Chart</p>
                      <p className="text-xs text-gray-500">Conversion rates</p>
                    </div>
                    <div className="bg-emerald-50 px-3 py-2 rounded-lg">
                      <p className="text-sm font-semibold text-emerald-600">Financial Heartbeat</p>
                      <p className="text-xs text-gray-500">Live ledger status</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trust Score Dashboard Card */}
            <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 mt-4">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Shield className="h-5 w-5 text-emerald-600" />
                      Trust Score Dashboard
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      Configure trust ranking influence and monitor clinic scores
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate('/treatx-admin/trust-score')}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600"
                    data-testid="open-trust-score-dashboard-btn"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Trust Scores
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-white rounded-lg p-6 border text-center">
                  <Shield className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
                  <h4 className="font-medium text-gray-700 mb-2">Clinic Trust Score Engine</h4>
                  <p className="text-sm text-gray-500 max-w-md mx-auto">
                    View computed trust scores, adjust ranking influence weight, 
                    and inspect score breakdowns per clinic.
                  </p>
                  <div className="flex justify-center gap-4 mt-4 flex-wrap">
                    <div className="bg-emerald-50 px-3 py-2 rounded-lg">
                      <p className="text-sm font-semibold text-emerald-600">Weight Config</p>
                      <p className="text-xs text-gray-500">Slider + input</p>
                    </div>
                    <div className="bg-blue-50 px-3 py-2 rounded-lg">
                      <p className="text-sm font-semibold text-blue-600">Score Table</p>
                      <p className="text-xs text-gray-500">Sortable rankings</p>
                    </div>
                    <div className="bg-amber-50 px-3 py-2 rounded-lg">
                      <p className="text-sm font-semibold text-amber-600">Breakdown</p>
                      <p className="text-xs text-gray-500">Per-clinic detail</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Campaign Alert Stats Card */}
            <CampaignAlertStatsCard />
          </TabsContent>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <MessageSquare className="h-5 w-5 text-blue-600" />
                      Contact Inquiries
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      Manage messages from the contact form
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate('/treatx-admin/inquiries')}
                    className="bg-gradient-to-r from-blue-600 to-cyan-600"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Inquiries Panel
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-white rounded-lg p-6 border text-center">
                  <MessageSquare className="h-12 w-12 text-blue-400 mx-auto mb-3" />
                  <h4 className="font-medium text-gray-700 mb-2">Centralized Message Management</h4>
                  <p className="text-sm text-gray-500 max-w-md mx-auto">
                    View, respond to, and manage all contact form submissions. 
                    Track inquiry status from new to resolved.
                  </p>
                  <div className="flex justify-center gap-4 mt-4">
                    <div className="bg-blue-50 px-4 py-2 rounded-lg">
                      <p className="text-xl font-bold text-blue-600">New</p>
                      <p className="text-xs text-gray-500">Unread messages</p>
                    </div>
                    <div className="bg-amber-50 px-4 py-2 rounded-lg">
                      <p className="text-xl font-bold text-amber-600">In Progress</p>
                      <p className="text-xs text-gray-500">Being handled</p>
                    </div>
                    <div className="bg-green-50 px-4 py-2 rounded-lg">
                      <p className="text-xl font-bold text-green-600">Resolved</p>
                      <p className="text-xs text-gray-500">Completed</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab - Phase 1 Batch 1.3 */}
          <TabsContent value="applications">
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Building2 className="h-5 w-5 text-purple-600" />
                      Clinic Applications
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      Review and manage clinic registration requests
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate('/treatx-admin/provider-domain')}
                    className="bg-gradient-to-r from-purple-600 to-pink-600"
                    data-testid="open-applications-panel-btn"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Applications Panel
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-white rounded-lg p-6 border text-center">
                  <Building2 className="h-12 w-12 text-purple-400 mx-auto mb-3" />
                  <h4 className="font-medium text-gray-700 mb-2">Clinic Registration Management</h4>
                  <p className="text-sm text-gray-500 max-w-md mx-auto">
                    Review new clinic applications, verify documents, 
                    approve or reject registrations, and request additional information.
                  </p>
                  <div className="flex justify-center gap-4 mt-4">
                    <div className="bg-amber-50 px-4 py-2 rounded-lg">
                      <p className="text-xl font-bold text-amber-600">Pending</p>
                      <p className="text-xs text-gray-500">Awaiting review</p>
                    </div>
                    <div className="bg-green-50 px-4 py-2 rounded-lg">
                      <p className="text-xl font-bold text-green-600">Approved</p>
                      <p className="text-xs text-gray-500">Active clinics</p>
                    </div>
                    <div className="bg-red-50 px-4 py-2 rounded-lg">
                      <p className="text-xl font-bold text-red-600">Rejected</p>
                      <p className="text-xs text-gray-500">Declined apps</p>
                    </div>
                    <div className="bg-blue-50 px-4 py-2 rounded-lg">
                      <p className="text-xl font-bold text-blue-600">Info Needed</p>
                      <p className="text-xs text-gray-500">More docs required</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Templates Tab - P1 */}
          <TabsContent value="emails">
            <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle className="flex items-center gap-2 text-xl">
                        <Mail className="h-5 w-5 text-amber-600" />
                        Email Templates
                      </CardTitle>
                      <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid="email-templates-backend-badge">BACKEND-CONNECTED</Badge>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      Configure notification templates for clinic lifecycle events.
                      Templates are stored server-side and rendered through the canonical NotificationService.
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate('/treatx-admin/email-templates')}
                    className="bg-gradient-to-r from-amber-500 to-orange-500"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Templates Panel
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-white rounded-lg p-6 border text-center">
                  <Mail className="h-12 w-12 text-amber-400 mx-auto mb-3" />
                  <h4 className="font-medium text-gray-700 mb-2">Email Notification Management</h4>
                  <p className="text-sm text-gray-500 max-w-md mx-auto">
                    Customize email templates sent during clinic registration, 
                    trial periods, and subscription changes. Preview templates with mock data.
                  </p>
                  <div className="flex justify-center gap-4 mt-4 flex-wrap">
                    <div className="bg-blue-50 px-3 py-2 rounded-lg">
                      <p className="text-sm font-semibold text-blue-600">Application Events</p>
                      <p className="text-xs text-gray-500">4 templates</p>
                    </div>
                    <div className="bg-teal-50 px-3 py-2 rounded-lg">
                      <p className="text-sm font-semibold text-teal-600">Trial Events</p>
                      <p className="text-xs text-gray-500">3 templates</p>
                    </div>
                    <div className="bg-indigo-50 px-3 py-2 rounded-lg">
                      <p className="text-sm font-semibold text-indigo-600">Subscription Events</p>
                      <p className="text-xs text-gray-500">1 template</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* TREATX-COMMERCIAL-GATING-ADMIN-HARDENING-09B */}
            <Card className="mt-4 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="commercial-gating-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle className="flex items-center gap-2 text-xl">
                        <Shield className="h-5 w-5 text-emerald-600" />
                        External Communication Policy
                      </CardTitle>
                      <Badge className="bg-emerald-500 text-white">LIVE</Badge>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      Controls when patient ↔ provider direct WhatsApp / SMS contact is unlocked.
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/treatx-admin/commercial-gating')}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500"
                    data-testid="open-commercial-gating-btn"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Policy Panel
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {/* TREATX-PRODUCT-LANGUAGES-ADMIN-01 */}
            <Card className="mt-4 border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50" data-testid="product-languages-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle className="flex items-center gap-2 text-xl">
                        <Globe className="h-5 w-5 text-indigo-600" />
                        Product Languages
                      </CardTitle>
                      <Badge className="bg-indigo-500 text-white">RUNTIME</Badge>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      Controls which supported locales users can select in the product language switcher.
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/treatx-admin/product-languages')}
                    className="bg-gradient-to-r from-indigo-500 to-blue-500"
                    data-testid="open-product-languages-btn"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Manage Languages
                  </Button>
                </div>
              </CardHeader>
            </Card>
          </TabsContent>

          {/* MESSAGING & ESCALATIONS TAB */}
          <TabsContent value="messaging">
            <Card data-testid="messaging-management-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <MessageSquare className="h-5 w-5 text-blue-600" />
                      Messaging Oversight & Escalations
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      Monitor all platform conversations, handle escalated threads, intervene when needed
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/treatx-admin/messaging')}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600"
                    data-testid="open-messaging-panel-btn"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Full Messaging Panel
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-white rounded-lg p-6 border text-center">
                  <MessageSquare className="h-12 w-12 text-blue-400 mx-auto mb-3" />
                  <h4 className="font-medium text-gray-700 mb-2">Platform Messaging Hub</h4>
                  <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">
                    View all threads across clinics and patients. Escalated threads from users needing admin attention appear in the dedicated Escalations tab.
                  </p>
                  <div className="flex justify-center gap-4 mt-4 flex-wrap">
                    <div className="bg-red-50 px-4 py-3 rounded-lg cursor-pointer hover:bg-red-100 transition-colors" onClick={() => navigate('/treatx-admin/messaging')}>
                      <AlertTriangle className="h-5 w-5 text-red-600 mx-auto mb-1" />
                      <p className="text-sm font-semibold text-red-600">Escalated Threads</p>
                      <p className="text-xs text-gray-500">Requires admin attention</p>
                    </div>
                    <div className="bg-blue-50 px-4 py-3 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => navigate('/treatx-admin/messaging')}>
                      <MessageSquare className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                      <p className="text-sm font-semibold text-blue-600">All Threads</p>
                      <p className="text-xs text-gray-500">Platform-wide overview</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assignments Tab — Phase 1.5 system-wide visibility */}
          <TabsContent value="assignments">
            <AssignmentOverviewTab />
          </TabsContent>

          {/* Aftercare Tab — Phase 2 */}
          <TabsContent value="aftercare">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <HeartPulse className="h-5 w-5 text-emerald-600" /> System-wide Aftercare
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AftercarePanel userRole="treatx_super_admin" />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Upgrade Requests Tab */}
          <TabsContent value="upgrade-requests">
            <UpgradeRequestsPanel />
          </TabsContent>
          {/* document-rules tab removed — unified into /treatx-admin/provider-domain */}
          <TabsContent value="document-rules">
            <Card className="border-2 border-slate-200">
              <CardContent className="py-8 text-center">
                <p className="text-sm text-slate-600 mb-3">Document Types and Requirement Rules management has moved to the unified Provider Management panel.</p>
                <Button onClick={() => navigate('/treatx-admin/provider-domain')} className="bg-gradient-to-r from-indigo-600 to-purple-600" data-testid="go-provider-domain-from-doc-rules">
                  <ExternalLink className="h-4 w-4 mr-2" /> Open Provider Management
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      {editModal.open && (
        <TreatmentEditorModal
          treatment={editModal.treatment}
          categories={activeCategories}
          onSave={handleSaveTreatment}
          onClose={() => setEditModal({ open: false, treatment: null })}
        />
      )}
      
      {/* Build Version Stamp — preview/dev only. Hidden on production
          hosts (treatx.health / *.treatx.health) per
          PRE-UAT-BUILD-BADGE-PROD-HIDE-01. Mirrors the production-host
          gate already used by EnvBadge/VersionBadge so the production
          gate / login is never overlapped by a debug widget. */}
      {(() => {
        const h = (typeof window !== 'undefined' ? (window.location.hostname || '') : '').toLowerCase();
        const isProd = h === 'treatx.health' || h === 'www.treatx.health' || h.endsWith('.treatx.health');
        if (isProd) return null;
        return (
          <div className="fixed bottom-4 right-4 bg-gray-800 text-white text-xs px-3 py-1 rounded-full opacity-75 z-50" data-testid="build-stamp">
            Build: admin-dash-v4
          </div>
        );
      })()}
    </div>
  );
};

export default TreatXAdminDashboard;
